// metrics_manager.jsx
// Navela usage-metrics dashboard. Read-only reporting over the usage_events log
// (written by the Navela ingestion server on POST /api/events) via the admin
// backend's navela-metrics package. Admins pick a date window + platform and can
// see, break down and filter: top-line usage, a trend over time, which SHIPS get
// the most booking backlink taps (official / compare / affiliate), which screens &
// elements get used, and a raw, filterable event explorer.
//
// Dataviz notes: magnitude is a single-hue (pr-navy) bar length; the backlink-kind
// split is the one categorical encoding — a fixed 3-hue order (validated
// colorblind-safe) always shown with a legend + direct labels; KPIs are plain ink
// stat tiles; change-over-time is a single-series bar trend with per-bar hover.

import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  BarChart3, Ship, MousePointerClick, Eye, Search, RefreshCw, ChevronLeft, ChevronRight,
  Layers, Monitor, TrendingUp, ListFilter, ExternalLink,
} from 'lucide-react';
import { useNavela } from '../../authentication_components/navelaContext';
import { Modal, inputClass, Button, Loading, EmptyState } from './navela_ui';

// Fixed categorical order for the backlink-kind split. Validated colorblind-safe
// (blue / orange / violet, ΔE ≥ 23 across CVD types); always paired with a legend
// and text labels so identity is never colour-alone.
const KIND_ORDER = ['official', 'compare', 'affiliate'];
const KIND_COLORS = { official: '#0284c7', compare: '#ea580c', affiliate: '#7c3aed' };
const KIND_LABELS = { official: 'Official site', compare: 'Compare search', affiliate: 'Affiliate' };

const PLATFORMS = ['web', 'ios', 'android'];
const TREND_METRICS = [
  { id: 'events', label: 'Events' },
  { id: 'sessions', label: 'Sessions' },
  { id: 'backlink_taps', label: 'Backlink taps' },
];
const EVENTS_PAGE_SIZE = 50;

// ── date helpers (windows are inclusive in the UI; the API upper bound is
// exclusive, so we send `to`+1 day). All in UTC — fine for admin reporting. ──
const isoDate = (d) => d.toISOString().slice(0, 10);
const addDays = (dateStr, n) => {
  const d = new Date(`${dateStr}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return isoDate(d);
};
const todayStr = () => isoDate(new Date());

const fmtInt = (n) => (n ?? 0).toLocaleString();
const fmtDateTime = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};
const fmtBucket = (v) => {
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

export default function MetricsManager() {
  const {
    fetchMetricsSummary, fetchMetricsFilters, fetchMetricsTimeseries,
    fetchShipBacklinks, fetchElementUsage, fetchScreenViews, fetchMetricsEvents,
  } = useNavela();

  // Global filters (drive every section).
  const [fromDate, setFromDate] = useState(() => addDays(todayStr(), -29));
  const [toDate, setToDate] = useState(() => todayStr());
  const [platform, setPlatform] = useState('');

  // Report data.
  const [summary, setSummary] = useState(null);
  const [series, setSeries] = useState([]);
  const [ships, setShips] = useState([]);
  const [screens, setScreens] = useState([]);
  const [elements, setElements] = useState([]);
  const [options, setOptions] = useState(null);

  // Section-local filters.
  const [trendMetric, setTrendMetric] = useState('events');
  const [shipKind, setShipKind] = useState('');
  const [shipPartner, setShipPartner] = useState('');
  const [elementScreen, setElementScreen] = useState('');

  // Event explorer.
  const [evName, setEvName] = useState('');
  const [evScreen, setEvScreen] = useState('');
  const [evSearchInput, setEvSearchInput] = useState('');
  const [evSearch, setEvSearch] = useState('');
  const [evOffset, setEvOffset] = useState(0);
  const [eventsData, setEventsData] = useState({ events: [], total_count: 0 });
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Per-section loading flags (context also flips a shared flag on each call).
  const [loadingCore, setLoadingCore] = useState(true);
  const [loadingShips, setLoadingShips] = useState(true);
  const [loadingUsage, setLoadingUsage] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(true);

  // API window: from as-is, to made exclusive (+1 day) so the end day is included.
  const apiRange = useMemo(() => ({ from: fromDate, to: addDays(toDate, 1) }), [fromDate, toDate]);

  // Filter option lists (loaded once).
  useEffect(() => {
    fetchMetricsFilters()
      .then((data) => setOptions(data || null))
      .catch(() => { /* dropdowns simply stay minimal */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Core: summary + trend + screen views (global filters only).
  useEffect(() => {
    let cancelled = false;
    setLoadingCore(true);
    Promise.all([
      fetchMetricsSummary({ from: apiRange.from, to: apiRange.to, platform: platform || undefined }),
      fetchMetricsTimeseries({ from: apiRange.from, to: apiRange.to, interval: 'day', platform: platform || undefined }),
      fetchScreenViews({ from: apiRange.from, to: apiRange.to, platform: platform || undefined }),
    ])
      .then(([sum, ts, scr]) => {
        if (cancelled) return;
        setSummary(sum || null);
        setSeries(Array.isArray(ts) ? ts : []);
        setScreens(Array.isArray(scr) ? scr : []);
      })
      .catch((err) => { if (!cancelled) toast.error(err.message || 'Failed to load metrics'); })
      .finally(() => { if (!cancelled) setLoadingCore(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRange.from, apiRange.to, platform]);

  // Ship backlinks (global filters + kind/partner).
  useEffect(() => {
    let cancelled = false;
    setLoadingShips(true);
    fetchShipBacklinks({
      from: apiRange.from, to: apiRange.to, platform: platform || undefined,
      kind: shipKind || undefined, partner: shipPartner || undefined, limit: 25,
    })
      .then((data) => { if (!cancelled) setShips(Array.isArray(data) ? data : []); })
      .catch((err) => { if (!cancelled) toast.error(err.message || 'Failed to load ship backlinks'); })
      .finally(() => { if (!cancelled) setLoadingShips(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRange.from, apiRange.to, platform, shipKind, shipPartner]);

  // Element usage (global filters + screen).
  useEffect(() => {
    let cancelled = false;
    setLoadingUsage(true);
    fetchElementUsage({ from: apiRange.from, to: apiRange.to, platform: platform || undefined, screen: elementScreen || undefined })
      .then((data) => { if (!cancelled) setElements(Array.isArray(data) ? data : []); })
      .catch((err) => { if (!cancelled) toast.error(err.message || 'Failed to load element usage'); })
      .finally(() => { if (!cancelled) setLoadingUsage(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRange.from, apiRange.to, platform, elementScreen]);

  // Debounce the event-explorer free-text search.
  useEffect(() => {
    const t = setTimeout(() => { setEvOffset(0); setEvSearch(evSearchInput.trim()); }, 350);
    return () => clearTimeout(t);
  }, [evSearchInput]);

  // Event explorer (global filters + its own filters + pagination).
  useEffect(() => {
    let cancelled = false;
    setLoadingEvents(true);
    fetchMetricsEvents({
      from: apiRange.from, to: apiRange.to, platform: platform || undefined,
      event_name: evName || undefined, screen: evScreen || undefined, search: evSearch || undefined,
      limit: EVENTS_PAGE_SIZE, offset: evOffset,
    })
      .then((data) => { if (!cancelled) setEventsData(data || { events: [], total_count: 0 }); })
      .catch((err) => { if (!cancelled) toast.error(err.message || 'Failed to load events'); })
      .finally(() => { if (!cancelled) setLoadingEvents(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRange.from, apiRange.to, platform, evName, evScreen, evSearch, evOffset]);

  const applyPreset = (days) => { setFromDate(addDays(todayStr(), -(days - 1))); setToDate(todayStr()); };
  const refreshAll = () => {
    // Explicit reload of every section (works even when filters are unchanged, so
    // the effects wouldn't otherwise re-fire).
    setLoadingCore(true); setLoadingShips(true); setLoadingUsage(true); setLoadingEvents(true);
    Promise.allSettled([
      fetchMetricsSummary({ from: apiRange.from, to: apiRange.to, platform: platform || undefined }).then((d) => setSummary(d || null)),
      fetchMetricsTimeseries({ from: apiRange.from, to: apiRange.to, interval: 'day', platform: platform || undefined }).then((d) => setSeries(Array.isArray(d) ? d : [])),
      fetchScreenViews({ from: apiRange.from, to: apiRange.to, platform: platform || undefined }).then((d) => setScreens(Array.isArray(d) ? d : [])),
      fetchShipBacklinks({ from: apiRange.from, to: apiRange.to, platform: platform || undefined, kind: shipKind || undefined, partner: shipPartner || undefined, limit: 25 }).then((d) => setShips(Array.isArray(d) ? d : [])),
      fetchElementUsage({ from: apiRange.from, to: apiRange.to, platform: platform || undefined, screen: elementScreen || undefined }).then((d) => setElements(Array.isArray(d) ? d : [])),
      fetchMetricsEvents({ from: apiRange.from, to: apiRange.to, platform: platform || undefined, event_name: evName || undefined, screen: evScreen || undefined, search: evSearch || undefined, limit: EVENTS_PAGE_SIZE, offset: evOffset }).then((d) => setEventsData(d || { events: [], total_count: 0 })),
    ]).finally(() => { setLoadingCore(false); setLoadingShips(false); setLoadingUsage(false); setLoadingEvents(false); });
  };

  const screenOptions = options?.screens || [];
  const partnerOptions = options?.partners || [];
  const eventNameOptions = options?.event_names || [];

  const evPage = Math.floor(evOffset / EVENTS_PAGE_SIZE) + 1;
  const evPageCount = Math.max(1, Math.ceil((eventsData.total_count || 0) / EVENTS_PAGE_SIZE));

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      {/* Heading */}
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-bold text-slate-800">
            <BarChart3 className="h-5 w-5 text-pr-navy-600" /> Usage Metrics
          </h2>
          <p className="text-sm text-slate-500">First-party product analytics across the Navela webapp &amp; mobile app.</p>
        </div>
        <Button variant="secondary" onClick={refreshAll} title="Refresh">
          <RefreshCw className={`h-4 w-4 ${loadingCore ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Global filter row */}
      <div className="mb-6 flex flex-wrap items-end gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">From</span>
          <input type="date" value={fromDate} max={toDate} onChange={(e) => setFromDate(e.target.value)} className={`${inputClass} w-40`} />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">To</span>
          <input type="date" value={toDate} min={fromDate} max={todayStr()} onChange={(e) => setToDate(e.target.value)} className={`${inputClass} w-40`} />
        </label>
        <div className="flex items-center gap-1">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => applyPreset(d)}
              className="rounded-lg border border-slate-200 px-2.5 py-2 text-xs font-semibold text-slate-500 transition-colors hover:bg-slate-50 hover:text-pr-navy-600">
              {d}d
            </button>
          ))}
        </div>
        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Platform</span>
          <select value={platform} onChange={(e) => setPlatform(e.target.value)} className={`${inputClass} w-40`}>
            <option value="">All platforms</option>
            {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>
      </div>

      {/* KPI stat tiles */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <StatTile label="Events" value={summary?.events} icon={Layers} loading={loadingCore} />
        <StatTile label="Sessions" value={summary?.sessions} icon={TrendingUp} loading={loadingCore} />
        <StatTile label="Devices" value={summary?.devices} icon={Monitor} loading={loadingCore} />
        <StatTile label="Backlink taps" value={summary?.backlink_taps} icon={MousePointerClick} loading={loadingCore} accent />
        <StatTile label="Ship views" value={summary?.ship_views} icon={Ship} loading={loadingCore} />
        <StatTile label="Screen views" value={summary?.screen_views} icon={Eye} loading={loadingCore} />
      </div>

      {/* Trend */}
      <Section
        title="Activity over time"
        icon={TrendingUp}
        right={
          <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-0.5">
            {TREND_METRICS.map((m) => (
              <button key={m.id} onClick={() => setTrendMetric(m.id)}
                className={`rounded-md px-2.5 py-1 text-xs font-semibold transition-colors ${
                  trendMetric === m.id ? 'bg-white text-pr-navy-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}>
                {m.label}
              </button>
            ))}
          </div>
        }
      >
        {loadingCore ? <Loading label="Loading trend…" />
          : <TrendBars data={series} metric={trendMetric} />}
      </Section>

      {/* Ship backlinks */}
      <Section
        title="Top ships by booking backlink taps"
        icon={Ship}
        right={
          <div className="flex flex-wrap items-center gap-2">
            <KindLegend />
            <select value={shipKind} onChange={(e) => setShipKind(e.target.value)} className={`${inputClass} h-9 w-36 py-1`}>
              <option value="">Any kind</option>
              {KIND_ORDER.map((k) => <option key={k} value={k}>{KIND_LABELS[k]}</option>)}
            </select>
            {partnerOptions.length > 0 && (
              <select value={shipPartner} onChange={(e) => setShipPartner(e.target.value)} className={`${inputClass} h-9 w-40 py-1`}>
                <option value="">Any partner</option>
                {partnerOptions.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            )}
          </div>
        }
      >
        {loadingShips ? <Loading label="Loading ship backlinks…" />
          : ships.length === 0 ? <EmptyState icon={Ship} title="No backlink taps in this window" hint="Backlink taps appear here once users tap a ship's booking option." />
            : <ShipBacklinkTable ships={ships} />}
      </Section>

      {/* Screens + elements */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Section title="Most-viewed screens" icon={Eye}>
          {loadingCore ? <Loading label="Loading screens…" />
            : screens.length === 0 ? <EmptyState icon={Eye} title="No screen views yet" />
              : <MagnitudeList items={screens.slice(0, 12).map((s) => ({ label: s.screen || '(unknown)', value: s.views }))} />}
        </Section>

        <Section
          title="Most-used elements"
          icon={MousePointerClick}
          right={
            screenOptions.length > 0 && (
              <select value={elementScreen} onChange={(e) => setElementScreen(e.target.value)} className={`${inputClass} h-9 w-44 py-1`}>
                <option value="">All screens</option>
                {screenOptions.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            )
          }
        >
          {loadingUsage ? <Loading label="Loading elements…" />
            : elements.length === 0 ? <EmptyState icon={MousePointerClick} title="No element usage yet" />
              : <MagnitudeList items={elements.slice(0, 12).map((e) => ({
                  label: e.element || '(unknown)', sub: e.screen, value: e.count,
                }))} />}
        </Section>
      </div>

      {/* Raw event explorer */}
      <Section title="Event explorer" icon={ListFilter}>
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="relative min-w-[220px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input value={evSearchInput} onChange={(e) => setEvSearchInput(e.target.value)}
              placeholder="Search ship, screen, element, partner, URL…" className={`${inputClass} pl-9`} />
          </div>
          <select value={evName} onChange={(e) => { setEvOffset(0); setEvName(e.target.value); }} className={`${inputClass} w-48`}>
            <option value="">Any event</option>
            {(eventNameOptions.length ? eventNameOptions : ['app_open', 'screen_view', 'ship_view', 'element_view', 'element_interact', 'search', 'backlink_tap']).map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
          {screenOptions.length > 0 && (
            <select value={evScreen} onChange={(e) => { setEvOffset(0); setEvScreen(e.target.value); }} className={`${inputClass} w-44`}>
              <option value="">Any screen</option>
              {screenOptions.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
        </div>

        {loadingEvents && (eventsData.events || []).length === 0 ? (
          <Loading label="Loading events…" />
        ) : (eventsData.events || []).length === 0 ? (
          <EmptyState icon={ListFilter} title="No events match these filters" hint="Widen the date window or clear a filter." />
        ) : (
          <div className="overflow-hidden rounded-xl border border-slate-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-100 text-sm">
                <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Event</th>
                    <th className="px-4 py-3">Platform</th>
                    <th className="px-4 py-3">Screen / element</th>
                    <th className="px-4 py-3">Ship</th>
                    <th className="px-4 py-3">When</th>
                    <th className="px-4 py-3 text-right">Detail</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {(eventsData.events || []).map((e) => (
                    <tr key={e.id} className="hover:bg-slate-50/60">
                      <td className="px-4 py-3"><EventBadge name={e.event_name} /></td>
                      <td className="px-4 py-3 text-slate-600">{e.platform}</td>
                      <td className="px-4 py-3 text-slate-600">
                        <div className="font-medium text-slate-700">{e.screen || <span className="text-slate-300">—</span>}</div>
                        {e.element && <div className="text-xs text-slate-400">{e.element}{e.action ? ` · ${e.action}` : ''}</div>}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {e.ship_name || e.ship_id ? (
                          <div>
                            <div className="font-medium text-slate-700">{e.ship_name || e.ship_id}</div>
                            {e.backlink_kind && <KindTag kind={e.backlink_kind} />}
                          </div>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-slate-500">{fmtDateTime(e.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => setSelectedEvent(e)} className="rounded-lg p-2 text-slate-400 hover:bg-pr-navy-50 hover:text-pr-navy-600" title="View event">
                          <Eye className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-sm text-slate-500">
              <span>{fmtInt(eventsData.total_count)} event{eventsData.total_count === 1 ? '' : 's'} · page {evPage} of {evPageCount}</span>
              <div className="flex items-center gap-1">
                <button disabled={evOffset === 0} onClick={() => setEvOffset(Math.max(0, evOffset - EVENTS_PAGE_SIZE))} className="rounded-lg p-2 hover:bg-slate-100 disabled:opacity-40"><ChevronLeft className="h-4 w-4" /></button>
                <button disabled={evPage >= evPageCount} onClick={() => setEvOffset(evOffset + EVENTS_PAGE_SIZE)} className="rounded-lg p-2 hover:bg-slate-100 disabled:opacity-40"><ChevronRight className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
        )}
      </Section>

      <EventDetailModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />
    </div>
  );
}

// ── Presentational pieces ──────────────────────────────────────────────────────

function Section({ title, icon: Icon, right, children }) {
  return (
    <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-600">
          {Icon && <Icon className="h-4 w-4 text-pr-navy-500" />} {title}
        </h3>
        {right && <div>{right}</div>}
      </div>
      {children}
    </div>
  );
}

function StatTile({ label, value, icon: Icon, loading, accent }) {
  return (
    <div className={`rounded-2xl border p-4 shadow-sm ${accent ? 'border-pr-navy-100 bg-pr-navy-50/60' : 'border-slate-200 bg-white'}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
        {Icon && <Icon className={`h-4 w-4 ${accent ? 'text-pr-navy-500' : 'text-slate-300'}`} />}
      </div>
      <div className="mt-2 text-2xl font-extrabold tabular-nums text-slate-800">
        {loading ? <span className="inline-block h-7 w-16 animate-pulse rounded bg-slate-100" /> : fmtInt(value)}
      </div>
    </div>
  );
}

// Single-series bar trend (change-over-time). Single hue (magnitude), 2px gaps,
// rounded tops, per-bar hover tooltip. One axis only.
function TrendBars({ data, metric }) {
  if (!data || data.length === 0) {
    return <EmptyState icon={TrendingUp} title="No activity in this window" hint="Pick a wider date range or a different platform." />;
  }
  const max = Math.max(1, ...data.map((d) => d[metric] || 0));
  return (
    <div>
      <div className="flex h-44 items-end gap-[2px]">
        {data.map((d, i) => {
          const v = d[metric] || 0;
          const pct = (v / max) * 100;
          return (
            <div key={d.bucket || i} className="group relative flex flex-1 items-end" style={{ minWidth: 2 }}>
              <div
                className="w-full rounded-t bg-pr-navy-500 transition-colors group-hover:bg-pr-navy-600"
                style={{ height: `${Math.max(v > 0 ? 2 : 0, pct)}%` }}
              />
              {/* hover tooltip */}
              <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1.5 hidden -translate-x-1/2 whitespace-nowrap rounded-lg bg-slate-800 px-2 py-1 text-xs text-white shadow-lg group-hover:block">
                <span className="font-semibold">{fmtInt(v)}</span> · {fmtBucket(d.bucket)}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex justify-between text-xs text-slate-400">
        <span>{fmtBucket(data[0].bucket)}</span>
        <span>{fmtBucket(data[data.length - 1].bucket)}</span>
      </div>
    </div>
  );
}

// Sequential magnitude list: label + single-hue bar + value. Bar length is the
// only magnitude encoding (one hue, never rank-coloured).
function MagnitudeList({ items }) {
  const max = Math.max(1, ...items.map((i) => i.value || 0));
  return (
    <ul className="space-y-2.5">
      {items.map((it, idx) => (
        <li key={`${it.label}-${it.sub || ''}-${idx}`}>
          <div className="mb-1 flex items-baseline justify-between gap-3">
            <span className="truncate text-sm font-medium text-slate-700" title={it.sub ? `${it.label} · ${it.sub}` : it.label}>
              {it.label}
              {it.sub && <span className="ml-1.5 text-xs font-normal text-slate-400">{it.sub}</span>}
            </span>
            <span className="shrink-0 text-sm font-semibold tabular-nums text-slate-600">{fmtInt(it.value)}</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-pr-navy-500" style={{ width: `${Math.max(2, (it.value / max) * 100)}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

// The backlink-kind legend (always present for the 3-colour categorical set).
function KindLegend() {
  return (
    <div className="flex items-center gap-3">
      {KIND_ORDER.map((k) => (
        <span key={k} className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
          <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: KIND_COLORS[k] }} /> {KIND_LABELS[k]}
        </span>
      ))}
    </div>
  );
}

function KindTag({ kind }) {
  const color = KIND_COLORS[kind] || '#64748b';
  return (
    <span className="mt-0.5 inline-flex items-center gap-1 text-xs font-medium" style={{ color }}>
      <span className="h-2 w-2 rounded-sm" style={{ backgroundColor: color }} /> {KIND_LABELS[kind] || kind}
    </span>
  );
}

// Top-ships table: taps + a categorical stacked split (official/compare/affiliate).
function ShipBacklinkTable({ ships }) {
  const maxTaps = Math.max(1, ...ships.map((s) => s.taps || 0));
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-100 text-sm">
        <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-4 py-3">Ship</th>
            <th className="px-4 py-3 w-1/3">Destination split</th>
            <th className="px-4 py-3 text-right">Taps</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {ships.map((s) => (
            <tr key={s.ship_id || s.ship_name} className="hover:bg-slate-50/60">
              <td className="px-4 py-3">
                <div className="font-semibold text-slate-800">{s.ship_name || s.ship_id || '(unknown ship)'}</div>
                {s.cruise_line && <div className="text-xs text-slate-400">{s.cruise_line}</div>}
              </td>
              <td className="px-4 py-3">
                <StackedKindBar byKind={s.by_kind} total={s.taps} />
              </td>
              <td className="px-4 py-3 text-right">
                <div className="text-sm font-bold tabular-nums text-slate-700">{fmtInt(s.taps)}</div>
                <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full rounded-full bg-pr-navy-300" style={{ width: `${Math.max(2, (s.taps / maxTaps) * 100)}%` }} />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Stacked categorical bar with a 2px surface gap between segments. Each nonzero
// segment carries a text count so identity/magnitude never rely on colour alone.
function StackedKindBar({ byKind, total }) {
  const bk = byKind || {};
  const sum = Math.max(1, total || (bk.official || 0) + (bk.compare || 0) + (bk.affiliate || 0));
  const present = KIND_ORDER.filter((k) => (bk[k] || 0) > 0);
  return (
    <div>
      <div className="flex h-4 w-full items-stretch gap-[2px] overflow-hidden rounded">
        {present.map((k) => (
          <div key={k} title={`${KIND_LABELS[k]}: ${fmtInt(bk[k])}`} style={{ backgroundColor: KIND_COLORS[k], flexGrow: bk[k], flexBasis: 0 }} className="first:rounded-l last:rounded-r" />
        ))}
        {present.length === 0 && <div className="w-full rounded bg-slate-100" />}
      </div>
      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-500">
        {KIND_ORDER.map((k) => (
          <span key={k} className="tabular-nums">
            <span className="font-medium" style={{ color: KIND_COLORS[k] }}>{KIND_LABELS[k]}</span> {fmtInt(bk[k] || 0)}
            {sum ? <span className="text-slate-400"> ({Math.round(((bk[k] || 0) / sum) * 100)}%)</span> : null}
          </span>
        ))}
      </div>
    </div>
  );
}

const EVENT_BADGE_STYLES = {
  backlink_tap: 'bg-pr-navy-50 text-pr-navy-700 ring-pr-navy-100',
  ship_view: 'bg-sky-50 text-sky-700 ring-sky-100',
  screen_view: 'bg-slate-100 text-slate-600 ring-slate-200',
  element_interact: 'bg-violet-50 text-violet-700 ring-violet-100',
  element_view: 'bg-slate-50 text-slate-600 ring-slate-200',
  search: 'bg-amber-50 text-amber-700 ring-amber-100',
  app_open: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
};

function EventBadge({ name }) {
  return (
    <span className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ${EVENT_BADGE_STYLES[name] || 'bg-slate-100 text-slate-600 ring-slate-200'}`}>
      {name}
    </span>
  );
}

function EventDetailModal({ event, onClose }) {
  if (!event) return null;
  const rows = [
    ['Event', event.event_name],
    ['Platform', event.platform],
    ['App version', event.app_version],
    ['Screen', event.screen],
    ['Element', event.element],
    ['Action', event.action],
    ['Ship', event.ship_name || event.ship_id],
    ['Cruise line', event.cruise_line],
    ['Backlink kind', event.backlink_kind],
    ['Partner', event.partner],
    ['Network', event.network],
    ['Locale', event.locale],
    ['Country', event.country],
    ['Session', event.session_id],
    ['Device', event.device_id],
    ['User', event.user_id],
    ['Created', fmtDateTime(event.created_at)],
    ['Received', fmtDateTime(event.received_at)],
  ].filter(([, v]) => v !== null && v !== undefined && v !== '');

  const hasMeta = event.metadata && Object.keys(event.metadata).length > 0;

  return (
    <Modal open title="Event" subtitle={`#${event.id} · ${event.event_name}`} onClose={onClose} wide
      footer={<Button variant="secondary" onClick={onClose}>Close</Button>}
    >
      <div className="space-y-5">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {rows.map(([k, v]) => (
            <div key={k}>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{k}</p>
              <p className="mt-0.5 break-words text-sm text-slate-700">{String(v)}</p>
            </div>
          ))}
        </div>
        {event.target_url && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Target URL</p>
            <a href={event.target_url} target="_blank" rel="noreferrer" className="mt-0.5 inline-flex items-center gap-1 break-all text-sm text-pr-navy-600 hover:underline">
              <ExternalLink className="h-3.5 w-3.5 shrink-0" /> {event.target_url}
            </a>
          </div>
        )}
        {hasMeta && (
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Metadata</p>
            <pre className="max-h-64 overflow-auto rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
              {JSON.stringify(event.metadata, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </Modal>
  );
}
